import {
  AvailabilityRequestAssignment,
  SelfScheduleAppointment,
} from "@/types/self-schedule";

import type { ProviderNextAvailabilityRequest } from "./self-schedule";
import {
  AvailabilityScope,
  cancelAppointment,
  checkIfPatientWithAppointments,
  checkPhoneBlacklistStatus,
  getAppointmentForms,
  getAppointmentFormsWithStatus,
  getAvailability,
  getBookingEvent,
  getCancellationPolicy,
  getFrontPage,
  getFrontPageImages,
  getProviderNextAvailability,
  selfScheduleAppointment,
} from "./self-schedule";

export const selfScheduleQueryKeys = {
  all: ["calendar", "self-schedule"] as const,
  frontPage: () => [...selfScheduleQueryKeys.all, "front-page"] as const,
  frontPageImages: () =>
    [...selfScheduleQueryKeys.all, "front-page-images"] as const,
  cancellationPolicy: (serviceIds: string[]) =>
    [...selfScheduleQueryKeys.all, "cancellation-policy", serviceIds] as const,
  allAvailability: () =>
    [...selfScheduleQueryKeys.all, "availability"] as const,
  availability: (
    assignments?: AvailabilityRequestAssignment[],
    scope?: AvailabilityScope,
    start?: Date,
    end?: Date,
    selectedAddons?: Record<string, string[]>,
    bookingEventId?: string | null,
    reschedulingEventId?: string | null,
  ) =>
    [
      ...selfScheduleQueryKeys.allAvailability(),
      assignments,
      scope,
      start?.toISOString(),
      end?.toISOString(),
      selectedAddons,
      bookingEventId,
      reschedulingEventId,
    ] as const,
  providerNextAvailability: (request: ProviderNextAvailabilityRequest) =>
    [
      ...selfScheduleQueryKeys.allAvailability(),
      "provider-next",
      request,
    ] as const,
  bookingEvent: (bookingEventId: string, reschedulingEventId?: string | null) =>
    [
      ...selfScheduleQueryKeys.all,
      "booking-event",
      bookingEventId,
      reschedulingEventId ?? null,
    ] as const,
  appointmentForms: (serviceId: string) =>
    [...selfScheduleQueryKeys.all, "service-forms", serviceId] as const,
  appointmentFormsWithStatus: (eventId: string, patientId: string) =>
    [
      ...selfScheduleQueryKeys.all,
      "service-forms-with-status",
      eventId,
      patientId,
    ] as const,
  checkIfPatientWithAppointments: (patientId: string) =>
    [
      ...selfScheduleQueryKeys.all,
      "check-if-patient-with-appointments",
      patientId,
    ] as const,
  checkPhoneBlacklistStatus: (phoneNumber: string) =>
    [
      ...selfScheduleQueryKeys.all,
      "check-phone-blacklist-status",
      phoneNumber,
    ] as const,
};

export const selfScheduleQueryOptions = {
  frontPage: () => ({
    queryKey: selfScheduleQueryKeys.frontPage(),
    queryFn: () => {
      return getFrontPage();
    },
  }),
  frontPageImages: () => ({
    queryKey: selfScheduleQueryKeys.frontPageImages(),
    queryFn: () => getFrontPageImages(),
  }),
  cancellationPolicy: (serviceIds: string[]) => ({
    queryKey: selfScheduleQueryKeys.cancellationPolicy(serviceIds),
    queryFn: () => getCancellationPolicy(serviceIds),
    enabled: serviceIds.length > 0,
  }),
  availability: (
    assignments: AvailabilityRequestAssignment[],
    start: Date,
    end: Date,
    scope: AvailabilityScope,
    selectedAddons?: Record<string, string[]>,
    bookingEventId?: string | null,
    reschedulingEventId?: string | null,
  ) => ({
    queryKey: selfScheduleQueryKeys.availability(
      assignments,
      scope,
      start,
      end,
      selectedAddons,
      bookingEventId,
      reschedulingEventId,
    ),
    queryFn: () => {
      return getAvailability(
        assignments,
        start,
        end,
        scope,
        selectedAddons,
        null,
        bookingEventId,
        reschedulingEventId,
      );
    },
  }),
  providerNextAvailability: (request: ProviderNextAvailabilityRequest) => ({
    queryKey: selfScheduleQueryKeys.providerNextAvailability(request),
    queryFn: () => getProviderNextAvailability(request),
  }),
  bookingEvent: (
    bookingEventId: string,
    reschedulingEventId?: string | null,
  ) => ({
    queryKey: selfScheduleQueryKeys.bookingEvent(
      bookingEventId,
      reschedulingEventId,
    ),
    queryFn: () => getBookingEvent(bookingEventId, reschedulingEventId),
  }),
  appointmentForms: (serviceId: string) => ({
    queryKey: selfScheduleQueryKeys.appointmentForms(serviceId),
    queryFn: () => getAppointmentForms(serviceId),
  }),
  appointmentFormsWithStatus: (eventId: string, patientId: string) => ({
    queryKey: selfScheduleQueryKeys.appointmentFormsWithStatus(
      eventId,
      patientId,
    ),
    queryFn: () => getAppointmentFormsWithStatus(eventId, patientId),
  }),
  checkIfPatientWithAppointments: (patientId: string) => ({
    queryKey: selfScheduleQueryKeys.checkIfPatientWithAppointments(patientId),
    queryFn: () => checkIfPatientWithAppointments(patientId),
  }),
  checkPhoneBlacklistStatus: (phoneNumber: string) => ({
    queryKey: selfScheduleQueryKeys.checkPhoneBlacklistStatus(phoneNumber),
    queryFn: () => checkPhoneBlacklistStatus(phoneNumber),
  }),
};

export const selfScheduleMutationOptions = {
  selfScheduleAppointment: () => ({
    mutationFn: (variables: SelfScheduleAppointment) => {
      return selfScheduleAppointment(variables);
    },
  }),
  cancelAppointment: () => ({
    mutationFn: (variables: {
      appointmentId: string;
      cancellationReason?: string;
    }) => {
      return cancelAppointment(
        variables.appointmentId,
        variables.cancellationReason,
      );
    },
  }),
};
